import { Check, Star } from "lucide-react";
import type { NaraBlock } from "@/mock";

/**
 * Simple, warm, on-brand representations of each generated block — what the
 * page would look like, rendered inside the live preview canvas. Coral stays
 * scarce here too: only the single primary CTA / "most popular" badge is coral.
 */
export function PreviewBlock({ block }: { block: NaraBlock }) {
  switch (block.type) {
    case "hero":
      return (
        <section className="px-6 py-10 text-center">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-subtle">
            aigen Studio
          </p>
          <h2 className="mx-auto mt-3 max-w-[18ch] text-[22px] font-semibold leading-tight tracking-tight text-ink-strong">
            See what's working.
          </h2>
          <p className="mx-auto mt-2 max-w-[26ch] text-[12px] leading-relaxed text-muted">
            {block.summary}
          </p>
          <div className="mt-5 flex items-center justify-center gap-2">
            <span className="rounded-btn bg-accent px-4 py-2 text-[12px] font-medium text-white">
              Start making
            </span>
            <span className="rounded-btn border border-hairline px-4 py-2 text-[12px] text-ink">
              See a demo
            </span>
          </div>
        </section>
      );

    case "features":
      return (
        <section className="px-6 py-8">
          <div className="grid grid-cols-3 gap-3">
            {["Signals", "Make", "Library"].map((f) => (
              <div key={f} className="rounded-card bg-paper-2 p-3 dark:bg-white/5">
                <div className="mb-2 h-6 w-6 rounded-btn bg-ink-strong/10 dark:bg-white/10" />
                <p className="text-[12px] font-semibold text-ink-strong">{f}</p>
                <div className="mt-1.5 space-y-1">
                  <div className="h-1.5 w-full rounded-full bg-hairline" />
                  <div className="h-1.5 w-2/3 rounded-full bg-hairline" />
                </div>
              </div>
            ))}
          </div>
        </section>
      );

    case "pricing":
      return (
        <section className="px-6 py-8">
          <div className="grid grid-cols-3 gap-3">
            {["Starter", "Scale", "Team"].map((tier, i) => {
              const popular = i === 1;
              return (
                <div
                  key={tier}
                  className={
                    "relative rounded-card border p-3 " +
                    (popular ? "border-accent/40 bg-paper" : "border-hairline bg-paper-2 dark:bg-white/5")
                  }
                >
                  {popular && (
                    <span className="absolute -top-2 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-chip bg-accent px-2 py-0.5 text-[8px] font-medium text-white">
                      <Star className="h-2.5 w-2.5" strokeWidth={2} /> Popular
                    </span>
                  )}
                  <p className="text-[11px] font-semibold text-ink-strong">{tier}</p>
                  <p className="mt-1 text-[16px] font-semibold tracking-tight text-ink-strong">
                    ${[0, 149, 399][i]}
                  </p>
                  <ul className="mt-2 space-y-1">
                    {[0, 1, 2].map((r) => (
                      <li key={r} className="flex items-center gap-1.5 text-[10px] text-muted">
                        <Check className="h-2.5 w-2.5 text-accent" strokeWidth={2.5} />
                        <span className="h-1.5 w-full rounded-full bg-hairline" />
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>
      );

    case "testimonial":
      return (
        <section className="px-6 py-8">
          <div className="rounded-card bg-paper-2 p-5 text-center dark:bg-white/5">
            <p className="font-serif text-[15px] italic leading-relaxed text-ink-strong">
              "It feels like Nara already knows what to fix."
            </p>
            <div className="mt-3 flex items-center justify-center gap-2">
              <span className="h-6 w-6 rounded-full bg-ink-strong/15 dark:bg-white/15" />
              <span className="text-[11px] text-muted">Lena Park · Marketing lead</span>
            </div>
          </div>
        </section>
      );

    case "faq":
      return (
        <section className="px-6 py-8">
          <div className="space-y-2">
            {["Is my data private?", "Can Nara use my brand kit?", "Do you support mobile replay?"].map(
              (q) => (
                <div key={q} className="rounded-card border border-hairline p-3">
                  <p className="text-[12px] font-medium text-ink-strong">{q}</p>
                  <div className="mt-1.5 h-1.5 w-3/4 rounded-full bg-hairline" />
                </div>
              ),
            )}
          </div>
        </section>
      );

    case "form":
      return (
        <section className="px-6 py-8">
          <div className="mx-auto max-w-[22ch] space-y-2.5 text-center">
            <h3 className="text-[15px] font-semibold tracking-tight text-ink-strong">
              Join the waitlist
            </h3>
            <div className="flex h-9 items-center rounded-btn border border-hairline bg-paper px-3 text-[11px] text-subtle">
              you@company.com
            </div>
            <span className="block rounded-btn bg-accent px-4 py-2 text-[12px] font-medium text-white">
              Request access
            </span>
            <p className="text-[10px] text-subtle">Join 4,980 others. No spam.</p>
          </div>
        </section>
      );

    case "cta":
      return (
        <section className="px-6 py-10 text-center">
          <h3 className="mx-auto max-w-[18ch] text-[18px] font-semibold tracking-tight text-ink-strong">
            Ready to see what's working?
          </h3>
          <span className="mt-4 inline-block rounded-btn bg-accent px-5 py-2.5 text-[13px] font-medium text-white">
            Start making
          </span>
        </section>
      );

    default:
      return null;
  }
}
